import type { Entity } from '../components'
import type { GameWorld } from '../world'
import { FEAT_TEAM_HALO } from '../species'
import { spawnPigmentPuff } from './particles'
import { T } from '../tunables'

function steer(e: Entity, tx: number, ty: number, speed: number, dt: number): void {
  const dx = tx - e.position.x
  const dy = ty - e.position.y
  const dist = Math.sqrt(dx * dx + dy * dy)
  if (dist < 0.001) return
  e.velocity!.x += ((dx / dist) * speed - e.velocity!.x) * Math.min(1, 10 * dt)
  e.velocity!.y += ((dy / dist) * speed - e.velocity!.y) * Math.min(1, 10 * dt)
}

export function allySystem(world: GameWorld, player: Entity, dt: number): void {
  const playerR = player.renderable?.radius ?? 0.12
  const orbitRadius = playerR * 3

  // Snapshot to avoid iterator invalidation when we add/remove components
  const allies = [...world.with('ally', 'position', 'velocity')]
  const livePredators = [...world.with('predator', 'position')]

  for (const e of allies) {
    const a = e.ally!

    // Tick follow timer; on expiry, dissolve back into a consumable microbe
    a.followTimer -= dt
    if (a.followTimer <= 0) {
      world.removeComponent(e, 'ally')
      world.addComponent(e, 'consumable', true)
      // Revert appearance to a normal microbe so it reads as food again
      if (e.renderable) {
        e.renderable.shape = 'microbe'
        e.renderable.tint = [0.5 + Math.random() * 0.4, 0.7 + Math.random() * 0.3, 1.0]
        e.renderable.radius = 0.05 + Math.random() * 0.04
      }
      // Drop the team-halo aura — this one's no longer "yours".
      if (e.morphology) e.morphology.featureFlags &= ~FEAT_TEAM_HALO
      // Microbes don't have eyes; remove so it reads as regular food again.
      if (e.eyes) world.removeComponent(e, 'eyes')
      continue
    }

    // Advance orbit angle
    a.orbitAngle += T.ally.orbitAngleSpeed * dt

    const orbitX = player.position.x + Math.cos(a.orbitAngle) * orbitRadius
    const orbitY = player.position.y + Math.sin(a.orbitAngle) * orbitRadius

    // Eye state mirrors charge state — focused (mid-anger) when charging,
    // friendly otherwise. Less feral than predators' 1.0 so the read is
    // "determined ally" not "another enemy". Auto-resets on any transition
    // back to orbiting/returning, including kills triggered from damage.ts.
    if (e.eyes) {
      e.eyes.angryTarget = a.state === 'charging' ? 0.55 : 0
    }

    if (a.state === 'orbiting') {
      steer(e, orbitX, orbitY, T.ally.orbitSpeed, dt)

      // Scan for a nearby predator to charge
      let nearest: Entity | null = null
      let nearestDist2 = T.ally.predatorDetectRadius * T.ally.predatorDetectRadius
      for (const pred of livePredators) {
        const dx = pred.position.x - e.position.x
        const dy = pred.position.y - e.position.y
        const d2 = dx * dx + dy * dy
        if (d2 < nearestDist2) {
          nearestDist2 = d2
          nearest = pred
        }
      }
      if (nearest) {
        a.state = 'charging'
        a.targetPredator = nearest
        // Charge-launch flourish — green pigment puff "they just lunged".
        const tint = e.renderable?.tint ?? [0.3, 1.0, 0.45]
        const radius = e.renderable?.radius ?? 0.08
        spawnPigmentPuff(world, e.position.x, e.position.y, tint, radius * 1.2)
      }
    } else if (a.state === 'charging') {
      // Check if target is still alive (world.id returns undefined for removed entities)
      if (!a.targetPredator || world.id(a.targetPredator) === undefined) {
        a.state = 'returning'
        a.targetPredator = null
      } else {
        steer(e, a.targetPredator.position.x, a.targetPredator.position.y, T.ally.chargeSpeed, dt)
      }
    } else {
      // returning
      steer(e, orbitX, orbitY, T.ally.returnSpeed, dt)
      const dx = orbitX - e.position.x
      const dy = orbitY - e.position.y
      if (dx * dx + dy * dy < T.ally.arrivalThreshold * T.ally.arrivalThreshold) {
        a.state = 'orbiting'
      }
    }
  }
}
